import { defineMessages } from "react-intl";

export const messages = defineMessages({
  adsManager: {
    id: "carpet_lite.organic.ads_manager",
    defaultMessage: "Ads Manager",
    description: "Product label shown in the organic ad preview header.",
  },
  heading: {
    id: "carpet_lite.organic.heading",
    defaultMessage: "See what your first ChatGPT ad could look like",
    description: "Heading introducing the website-based organic ad preview experience.",
  },
  description: {
    id: "carpet_lite.organic.description",
    defaultMessage:
      "Share your website and we’ll create an ad preview for you, then recommend a plan for you to reach the right customers. Nothing will go live until you approve it.",
    description: "Explanation of how a public website becomes an AI-generated ad preview.",
  },
  websiteLabel: {
    id: "carpet_lite.organic.website_label",
    defaultMessage: "Business website",
    description: "Accessible name of the public business website input.",
  },
  websitePlaceholder: {
    id: "carpet_lite.organic.website_placeholder",
    defaultMessage: "https://yourbusiness.com",
    description: "Example public business URL displayed in the website input.",
  },
  generate: {
    id: "carpet_lite.organic.generate",
    defaultMessage: "Create ad preview",
    description: "Action that generates an example ChatGPT ad from a business website.",
  },
  learnMore: {
    id: "carpet_lite.organic.learn_more",
    defaultMessage: "Learn more about ChatGPT Ads",
    description: "Link to information about advertising in ChatGPT.",
  },
  skipToAdsManager: {
    id: "carpet_lite.organic.skip_to_ads_manager",
    defaultMessage: "Skip to Ads Manager",
    description: "Header action that skips the organic ad-preview experience.",
  },
  thinking: {
    id: "carpet_lite.organic.thinking",
    defaultMessage: "Thinking",
    description: "Accessible progress heading shown while the example ad is being generated.",
  },
  thinkingDescription: {
    id: "carpet_lite.organic.thinking_description",
    defaultMessage:
      "We're reviewing relevant website information and proposing a campaign with a clicks objective.",
    description: "Explains the ad-generation work underway during the loading state.",
  },
  thinkingDetailsDescription: {
    id: "carpet_lite.organic.thinking_details_description",
    defaultMessage:
      "This campaign is designed to highlight the unique qualities of {website}. Our visual direction reflects the themes, products, and brand details that stand out across your website.",
    description: "Later-stage progress copy shown while the website-based ad is being generated.",
  },
  readyHeading: {
    id: "carpet_lite.organic.ready_heading",
    defaultMessage: "Your ChatGPT ad is ready",
    description: "Heading announcing that the generated ChatGPT ad preview is ready.",
  },
  readyDescription: {
    id: "carpet_lite.organic.ready_description",
    defaultMessage:
      "We built this ad for {businessName} using details that stood out across your website.",
    description: "Description of how the generated ad relates to the advertiser's website.",
  },
  readyDescriptionWithOneTheme: {
    id: "carpet_lite.organic.ready_description_with_one_theme",
    defaultMessage: "We built this ad around {theme}—a theme that stood out across your website.",
    description: "Explains the generated ad using one website-derived campaign theme.",
  },
  readyDescriptionWithTwoThemes: {
    id: "carpet_lite.organic.ready_description_with_two_themes",
    defaultMessage:
      "We built this ad around {firstTheme} and {secondTheme}—two themes that stood out across your website.",
    description: "Explains the generated ad using two website-derived campaign themes.",
  },
  reviewDetails: {
    id: "carpet_lite.organic.review_details",
    defaultMessage: "Review details",
    description: "Action that opens the generated campaign review page.",
  },
  reviewHeading: {
    id: "carpet_lite.organic.review_heading",
    defaultMessage: "Here’s your recommended plan",
    description: "Heading for the generated campaign settings review page.",
  },
  reviewDescription: {
    id: "carpet_lite.organic.review_description",
    defaultMessage:
      "We’re recommending this plan based on your business. Review the details below and adjust anything you’d like.",
    description: "Explains why the recommended campaign settings were selected.",
  },
  reviewDescriptionReach: {
    id: "carpet_lite.organic.review_description_reach",
    defaultMessage:
      "We recommend a reach campaign with settings designed to introduce your business to more potential customers.",
    description: "Explains the selected reach campaign settings.",
  },
  editCampaignDetails: {
    id: "carpet_lite.organic.edit_campaign_details",
    defaultMessage: "Edit campaign details",
    description: "Title and action label for editing generated campaign settings.",
  },
  editRecommendedPlan: {
    id: "carpet_lite.organic.edit_recommended_plan",
    defaultMessage: "Edit",
    description: "Visible action above the recommended campaign settings card.",
  },
  editCampaignDetailsDescription: {
    id: "carpet_lite.organic.edit_campaign_details_description",
    defaultMessage: "Update the campaign settings shown in your generated preview.",
    description: "Accessible description of the generated campaign settings dialog.",
  },
  closeEditCampaignDetails: {
    id: "carpet_lite.organic.close_edit_campaign_details",
    defaultMessage: "Close edit campaign details",
    description: "Accessible label for closing the campaign settings dialog.",
  },
  editAd: {
    id: "carpet_lite.organic.edit_ad",
    defaultMessage: "Edit ad",
    description: "Title and action label for editing generated ad creative.",
  },
  editAdPreview: {
    id: "carpet_lite.organic.edit_ad_preview",
    defaultMessage: "Edit ad preview",
    description: "Title of the generated ad preview editing dialog.",
  },
  editAdDescription: {
    id: "carpet_lite.organic.edit_ad_description",
    defaultMessage: "Update the advertiser, logo, title, subtext, or image shown in your ad.",
    description: "Accessible description of the generated ad editing dialog.",
  },
  closeEditAd: {
    id: "carpet_lite.organic.close_edit_ad",
    defaultMessage: "Close edit ad",
    description: "Accessible label for closing the generated ad editing dialog.",
  },
  advertiserName: {
    id: "carpet_lite.organic.advertiser_name",
    defaultMessage: "Advertiser name",
    description: "Label for the advertiser name shown in the generated ad.",
  },
  editAdvertiserName: {
    id: "carpet_lite.organic.edit_advertiser_name",
    defaultMessage: "Edit advertiser name",
    description: "Action revealing the editable advertiser name in the generated ad preview.",
  },
  businessLogo: {
    id: "carpet_lite.organic.business_logo",
    defaultMessage: "Business logo",
    description: "Label for the advertiser logo shown in the generated ad.",
  },
  logoImageUrl: {
    id: "carpet_lite.organic.logo_image_url",
    defaultMessage: "Image URL",
    description: "Label above the website used for the discovered advertiser logo.",
  },
  editBusinessLogo: {
    id: "carpet_lite.organic.edit_business_logo",
    defaultMessage: "Edit business logo",
    description: "Accessible label for opening the advertiser-logo and business-details editor.",
  },
  uploadBusinessLogo: {
    id: "carpet_lite.organic.upload_business_logo",
    defaultMessage: "Upload a logo",
    description: "Link that opens the advertiser-logo editor when no logo was discovered.",
  },
  reviewSuggestedBusinessDetails: {
    id: "carpet_lite.organic.review_suggested_business_details",
    defaultMessage: "and review your suggested business details.",
    description: "Guidance following the upload-a-logo link in the missing-logo warning.",
  },
  useLogoUrl: {
    id: "carpet_lite.organic.use_logo_url",
    defaultMessage: "Use URL",
    description: "Option for discovering an advertiser logo from an image or website URL.",
  },
  uploadLogoImage: {
    id: "carpet_lite.organic.upload_logo_image",
    defaultMessage: "Upload image",
    description: "Option for uploading an advertiser-logo image file.",
  },
  enterLogoUrl: {
    id: "carpet_lite.organic.enter_logo_url",
    defaultMessage: "Enter an image or website URL",
    description: "Label for the website or image URL used to find an advertiser logo.",
  },
  invalidLogoUrl: {
    id: "carpet_lite.organic.invalid_logo_url",
    defaultMessage: "Enter a valid public image or website URL.",
    description: "Safe validation shown when the advertiser-logo URL is invalid or nonpublic.",
  },
  uploadLogo: {
    id: "carpet_lite.organic.upload_logo",
    defaultMessage: "Upload logo",
    description: "Action that selects an advertiser logo for the generated ad.",
  },
  replaceLogo: {
    id: "carpet_lite.organic.replace_logo",
    defaultMessage: "Replace logo",
    description: "Action that replaces the advertiser logo shown in the generated ad.",
  },
  removeLogo: {
    id: "carpet_lite.organic.remove_logo",
    defaultMessage: "Remove logo",
    description: "Accessible action that removes the advertiser logo from the generated ad.",
  },
  logoUploadFailed: {
    id: "carpet_lite.organic.logo_upload_failed",
    defaultMessage: "We couldn't upload that logo. Please try another image.",
    description: "Safe error shown when an advertiser logo is rejected or cannot be uploaded.",
  },
  adTitle: {
    id: "carpet_lite.organic.ad_title",
    defaultMessage: "Title",
    description: "Label for the generated ad headline field.",
  },
  adDescription: {
    id: "carpet_lite.organic.ad_description",
    defaultMessage: "Subtext",
    description: "Label for the generated ad description field.",
  },
  adDescriptionTooLong: {
    id: "carpet_lite.organic.ad_description_too_long",
    defaultMessage: "Subtext must be {maxLength} characters or fewer.",
    description: "Validation shown when generated ad subtext exceeds its publishing limit.",
  },
  adImages: {
    id: "carpet_lite.organic.ad_images",
    defaultMessage: "Ad images",
    description: "Label for the generated ad image controls.",
  },
  adImagesHelp: {
    id: "carpet_lite.organic.ad_images_help",
    defaultMessage: "Upload a PNG or JPG. For best quality, use a square image (800 × 800 px).",
    description: "Format and size guidance shown above the generated ad image.",
  },
  removeAdImage: {
    id: "carpet_lite.organic.remove_ad_image",
    defaultMessage: "Remove ad image",
    description: "Accessible label for temporarily removing the generated ad image while editing.",
  },
  regenerateAdImage: {
    id: "carpet_lite.organic.regenerate_ad_image",
    defaultMessage: "Regenerate image",
    description: "Action that generates a replacement image for the ad.",
  },
  adImageRequired: {
    id: "carpet_lite.organic.ad_image_required",
    defaultMessage: "Regenerate an image before saving.",
    description: "Accessible status explaining why an ad without an image cannot be saved.",
  },
  adImageRegenerationFailed: {
    id: "carpet_lite.organic.ad_image_regeneration_failed",
    defaultMessage: "We couldn't regenerate the image. Please try again.",
    description: "Safe error shown when generated ad image regeneration fails.",
  },
  discard: {
    id: "carpet_lite.organic.discard",
    defaultMessage: "Discard",
    description: "Action that closes an edit dialog without applying changes.",
  },
  save: {
    id: "carpet_lite.organic.save",
    defaultMessage: "Save",
    description: "Action that applies generated preview edits.",
  },
  objectiveHelp: {
    id: "carpet_lite.organic.objective_help",
    defaultMessage:
      "Your campaign objective determines how you bid in the ad auction and what ad formats are available to promote.",
    description: "Helper text under the campaign objective setting.",
  },
  campaignLocations: {
    id: "carpet_lite.organic.campaign_locations",
    defaultMessage: "Locations for this campaign",
    description: "Label for the campaign location selector in the edit dialog.",
  },
  budget: {
    id: "carpet_lite.organic.budget",
    defaultMessage: "Budget",
    description: "Label for the campaign budget controls in the edit dialog.",
  },
  budgetType: {
    id: "carpet_lite.organic.budget_type",
    defaultMessage: "Budget type",
    description: "Accessible label for the campaign budget type selector.",
  },
  dailyBudget: {
    id: "carpet_lite.organic.daily_budget",
    defaultMessage: "Daily budget",
    description: "Daily campaign budget type option.",
  },
  campaignTotal: {
    id: "carpet_lite.organic.campaign_total",
    defaultMessage: "Campaign total",
    description: "Lifetime campaign budget type option.",
  },
  budgetAmount: {
    id: "carpet_lite.organic.budget_amount",
    defaultMessage: "Budget amount",
    description: "Accessible label for the campaign budget amount input.",
  },
  budgetHelp: {
    id: "carpet_lite.organic.budget_help",
    defaultMessage:
      "Your budget type (daily or campaign total) can’t be changed once this campaign has started. You can change your budget amount at any time.",
    description: "Helper text below campaign budget controls.",
  },
  invalidBudget: {
    id: "carpet_lite.organic.invalid_budget",
    defaultMessage: "Enter a valid budget greater than zero.",
    description: "Validation shown for an invalid generated campaign budget.",
  },
  objective: {
    id: "carpet_lite.organic.objective",
    defaultMessage: "Objective",
    description: "Label for the recommended campaign objective.",
  },
  clicks: {
    id: "carpet_lite.organic.clicks",
    defaultMessage: "Clicks",
    description: "Recommended campaign objective value.",
  },
  reach: {
    id: "carpet_lite.organic.reach",
    defaultMessage: "Reach",
    description: "Campaign objective option focused on reaching more people.",
  },
  locations: {
    id: "carpet_lite.organic.locations",
    defaultMessage: "Locations",
    description: "Label for the recommended campaign location.",
  },
  unitedStates: {
    id: "carpet_lite.organic.united_states",
    defaultMessage: "United States",
    description: "Recommended campaign location value.",
  },
  averageDailyBudget: {
    id: "carpet_lite.organic.average_daily_budget",
    defaultMessage: "Average daily budget",
    description: "Label for the recommended campaign daily budget.",
  },
  campaignTotalBudget: {
    id: "carpet_lite.organic.campaign_total_budget",
    defaultMessage: "Campaign total budget",
    description: "Label for the recommended campaign lifetime budget.",
  },
  averageDailyBudgetValue: {
    id: "carpet_lite.organic.average_daily_budget_value",
    defaultMessage: "$100",
    description: "Recommended campaign daily budget in US dollars.",
  },
  budgetValueInAccountCurrency: {
    id: "carpet_lite.organic.budget_value_in_account_currency",
    defaultMessage: "{amount} in your ad account currency",
    description: "Displays a pre-account budget without assuming the future ad account currency.",
  },
  bidStrategy: {
    id: "carpet_lite.organic.bid_strategy",
    defaultMessage: "Bid Strategy",
    description: "Label for the recommended campaign bid strategy.",
  },
  recommendedBidStrategy: {
    id: "carpet_lite.organic.recommended_bid_strategy",
    defaultMessage: "Bid strategy",
    description: "Sentence-case bid strategy label on the recommended campaign plan.",
  },
  maximumResults: {
    id: "carpet_lite.organic.maximum_results",
    defaultMessage: "Maximum results",
    description: "Recommended campaign bid strategy value.",
  },
  maximumResultsHelp: {
    id: "carpet_lite.organic.maximum_results_help",
    defaultMessage:
      "We’ll automatically adjust your bids to get as many clicks as possible within your budget.",
    description: "Explains the automatic maximum-results campaign bid strategy.",
  },
  manualBid: {
    id: "carpet_lite.organic.manual_bid",
    defaultMessage: "Manual bid",
    description: "Manual campaign bid strategy option.",
  },
  manualBidAmount: {
    id: "carpet_lite.organic.manual_bid_amount",
    defaultMessage: "Manual bid amount",
    description: "Accessible label for the manually selected maximum campaign bid amount.",
  },
  invalidManualBid: {
    id: "carpet_lite.organic.invalid_manual_bid",
    defaultMessage: "Enter a valid bid with up to two decimal places.",
    description: "Validation shown when a manual campaign bid is not a supported decimal amount.",
  },
  destinationUrl: {
    id: "carpet_lite.organic.destination_url",
    defaultMessage: "Destination URL",
    description: "Label for the generated ad destination URL.",
  },
  destinationUrlSuffixHelp: {
    id: "carpet_lite.organic.destination_url_suffix_help",
    defaultMessage:
      "The generated URL is locked. Add a path or query at the end for a more specific page.",
    description: "Explains that only the end of the generated destination URL may be edited.",
  },
  destinationUrlLockedHelp: {
    id: "carpet_lite.organic.destination_url_locked_help",
    defaultMessage:
      "This generated URL can’t be edited because it already includes a query or section.",
    description: "Explains why a generated destination URL with a query or hash is read-only.",
  },
  invalidDestinationUrlSuffix: {
    id: "carpet_lite.organic.invalid_destination_url_suffix",
    defaultMessage:
      "Keep the generated URL and add a path starting with / or a query starting with ?.",
    description: "Validation shown when a destination URL edit could change the generated base.",
  },
  launchCampaign: {
    id: "carpet_lite.organic.launch_campaign",
    defaultMessage: "Continue",
    description: "Action that continues the recommended campaign into account and billing setup.",
  },
  launchTerms: {
    id: "carpet_lite.organic.launch_terms",
    defaultMessage:
      'By clicking "Continue" you agree to the <termsLink>Advertising Tool Terms</termsLink>.',
    description: "Advertising terms notice shown below the campaign launch action.",
  },
  crawlingDisallowedError: {
    id: "carpet_lite.organic.crawling_disallowed_error",
    defaultMessage: "This website doesn't allow us to read its landing page.",
    description: "Error shown when robots.txt blocks the one-page Carpet Lite scrape.",
  },
  crawlingUnavailableError: {
    id: "carpet_lite.organic.crawling_unavailable_error",
    defaultMessage:
      "We couldn’t access this website to create an ad. Please check its crawler settings.",
    description:
      "Error shown when an advertiser's website cannot be crawled for campaign generation.",
  },
  crawlingGuidanceLink: {
    id: "carpet_lite.organic.crawling_guidance_link",
    defaultMessage: "Learn how to allow OpenAI web crawlers",
    description: "Link to advertiser guidance about allowing OpenAI's web crawlers.",
  },
  genericError: {
    id: "carpet_lite.organic.generic_error",
    defaultMessage: "We couldn't generate a campaign for that website. Please try again.",
    description: "Safe error shown when the website is invalid or ad generation fails.",
  },
  continuationError: {
    id: "carpet_lite.organic.continuation_error",
    defaultMessage: "We couldn't save your campaign details. Please try again.",
    description: "Safe error shown when the generated campaign cannot be carried into onboarding.",
  },
});
